import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import "./paneles.css";

/**
 * Las preguntas del panel, dentro del panel (22/10/2026).
 *
 * `window.prompt`, `confirm` y `alert` tapan la pantalla con una ventana del
 * navegador que no dice de qué pantalla es, en el móvil sale a medias y no se
 * puede leer bien un texto largo. Esto hace lo mismo con el estilo de los
 * paneles y la misma forma de usarse: se espera la respuesta y se sigue.
 *
 *   const { preguntar, pedir, avisar, dialogo } = useDialogo();
 *   if (!(await preguntar("¿Dar de baja «PC 1»?", "Dar de baja", true))) return;
 *   const motivo = await pedir("¿Por qué paras el reparto?", "parada manual");
 *   ...
 *   return <>{dialogo}…</>;
 */

type Abierto =
  | { tipo: "preguntar"; texto: string; si: string; peligro: boolean; resolver: (r: boolean) => void }
  | { tipo: "pedir"; texto: string; valor: string; resolver: (r: string | null) => void }
  | { tipo: "avisar"; texto: string; resolver: () => void };

export function useDialogo(): {
  preguntar: (texto: string, si?: string, peligro?: boolean) => Promise<boolean>;
  pedir: (texto: string, valor?: string) => Promise<string | null>;
  avisar: (texto: string) => Promise<void>;
  dialogo: ReactNode;
} {
  const [abierto, setAbierto] = useState<Abierto | null>(null);
  const [valor, setValor] = useState("");
  const campo = useRef<HTMLInputElement | null>(null);
  const boton = useRef<HTMLButtonElement | null>(null);

  const preguntar = useCallback(
    (texto: string, si = "Sí", peligro = false) =>
      new Promise<boolean>((resolver) => setAbierto({ tipo: "preguntar", texto, si, peligro, resolver })),
    [],
  );
  const pedir = useCallback(
    (texto: string, inicial = "") =>
      new Promise<string | null>((resolver) => {
        setValor(inicial);
        setAbierto({ tipo: "pedir", texto, valor: inicial, resolver });
      }),
    [],
  );
  const avisar = useCallback(
    (texto: string) => new Promise<void>((resolver) => setAbierto({ tipo: "avisar", texto, resolver })),
    [],
  );

  const cancelar = useCallback(() => {
    setAbierto((a) => {
      if (a?.tipo === "preguntar") a.resolver(false);
      else if (a?.tipo === "pedir") a.resolver(null);
      else a?.resolver();
      return null;
    });
  }, []);

  /* Escape cancela, y el foco va a donde se contesta. */
  useEffect(() => {
    if (!abierto) return;
    (abierto.tipo === "pedir" ? campo.current : boton.current)?.focus();
    const tecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelar();
    };
    document.addEventListener("keydown", tecla);
    return () => document.removeEventListener("keydown", tecla);
  }, [abierto, cancelar]);

  const aceptar = () => {
    if (!abierto) return;
    if (abierto.tipo === "preguntar") abierto.resolver(true);
    else if (abierto.tipo === "pedir") abierto.resolver(valor);
    else abierto.resolver();
    setAbierto(null);
  };

  const dialogo = abierto ? (
    <div className="dlg-fondo" onPointerDown={(e) => e.target === e.currentTarget && cancelar()}>
      <form
        className="dlg pnl"
        role="dialog"
        aria-modal="true"
        onSubmit={(e) => {
          e.preventDefault();
          aceptar();
        }}
      >
        <p className="dlg-texto">{abierto.texto}</p>
        {abierto.tipo === "pedir" && (
          <input ref={campo} className="dlg-campo" value={valor} onChange={(e) => setValor(e.target.value)} />
        )}
        <div className="dlg-botones">
          {abierto.tipo !== "avisar" && (
            <button type="button" className="pnl-boton plano" onClick={cancelar}>
              Cancelar
            </button>
          )}
          <button
            ref={boton}
            type="submit"
            className={`pnl-boton ${abierto.tipo === "preguntar" && abierto.peligro ? "no" : "si"}`}
          >
            {abierto.tipo === "preguntar" ? abierto.si : abierto.tipo === "pedir" ? "Aceptar" : "Entendido"}
          </button>
        </div>
      </form>
    </div>
  ) : null;

  return { preguntar, pedir, avisar, dialogo };
}
